import { compileAggregation, sanitizeFunnelDefinitionForSave } from '@/reports/funnel/utils';
import get from 'lodash/get';

export default {
    namespaced: true,
    state: () => ({
        aiAgentsById: {},
    }),
    mutations: {
        setAiAgent (state, agent) {
            if (agent && agent.id) {
                state.aiAgentsById[agent.id] = agent;
            }
        },
    },
    actions: {
        save ({ dispatch }, { funnel }) {
            const { definition } = funnel;
            const report = {
                ...funnel,
                shared: funnel.shared == null ? true : funnel.shared,
                aggregation: compileAggregation(funnel),
                definition: sanitizeFunnelDefinitionForSave({
                    ...definition,
                    type: 'Funnel',
                }),
            };

            return dispatch(funnel.id ? 'savedReports/update' : 'savedReports/create', { report }, { root: true });
        },
        async loadResources ({ dispatch, rootGetters, commit }, { id }) {
            await dispatch('savedReports/loadOne', { id }, { root: true });
            const funnel = rootGetters['savedReports/reportById'](id);
            if (!funnel) {
                throw new Error('Unable to find funnel with given id.');
            }

            const items = get(funnel, 'definition.config.items', []);
            const idSet = new Set();
            const resourcePromises = items.map(async (item) => {
                const { pageId, featureId, trackTypeId, guideId, agentId } = item;
                const id = pageId || featureId || trackTypeId || guideId || agentId;
                if (idSet.has(id)) return Promise.resolve();

                idSet.add(id);
                if (pageId) return dispatch('pages/loadOne', { id: pageId }, { root: true });
                if (featureId) return dispatch('features/loadOne', { id: featureId }, { root: true });
                if (trackTypeId) return dispatch('trackTypes/loadOne', { id: trackTypeId }, { root: true });
                if (guideId) return dispatch('guides/loadOne', { id: guideId }, { root: true });
                if (agentId) {
                    // Validate and cache the AI Agent so downstream consumers receive full resource data
                    const { fetchAiAgent } = await import('@/ai-agents/components/ai-agents.utils');
                    const agent = await fetchAiAgent(agentId);
                    commit('setAiAgent', agent);

                    return;
                }

                throw new Error('Funnel item is missing a resource id {pageId, featureId, trackTypeId, guideId, agentId}');
            });

            await Promise.all(resourcePromises);
        },
    },
    getters: {
        resources: (state, getters, rootState, rootGetters) => (id) => {
            const funnel = rootGetters['savedReports/reportById'](id);
            if (!funnel) return;

            const items = get(funnel, 'definition.config.items', []);

            return items.map((item) => {
                const { pageId, featureId, trackTypeId, guideId, agentId } = item;
                if (pageId) return rootGetters['pages/pageById'](pageId);
                if (featureId) return rootGetters['features/featureById'](featureId);
                if (trackTypeId) return rootGetters['trackTypes/trackTypeById'](trackTypeId);
                if (guideId) return rootGetters['guides/getGuideById'](guideId);
                if (agentId) return state.aiAgentsById[agentId];

                return null;
            });
        },
        allResourcesExist: (state, getters) => (id) => {
            return getters.resources(id).every(Boolean);
        },
    },
};
